/**
 * 「最近作品」thin api —— hub 页与工作台的 6 条横向带取数。
 *
 * ★直接打端点、自带最小投影,**不** import `@/features/*`:FSD 单向核心禁 features → pages,
 * 而这一份要同时被三个 hub 页(features 层)和工作台(pages 层)用。
 * 同 `entities/advisor-chat/api/creationsApi` 的既有做法。
 *
 * ★只取终态(bucket=history):这条带回答「我最近做出了什么」。在途任务归「进行中」节(首页 InFlightRow / `/works`)——
 * 一条 6 格的带里混进跑着的任务,用户会以为点进去能看成品。
 */
import { api } from "@/shared/api";
import {
  mapDh,
  mapPoster,
  mapPosterOneClickJob,
  mapReporter,
  mapVideo,
  type RawDhTask,
  type RawPosterTask,
  type RawReporterDraft,
  type RawVideoTask,
} from "../model/mappers";
import type { WorkItem, WorkLineKey } from "../model/types";

interface PageResult<T> {
  records: T[];
  total: number;
}

/** 带宽:6 格(2/3/6 列断点的公倍数,末行不残缺)。 */
export const RECENT_SIZE = 6;

type Bucket = "history" | "running";

async function listPoster(size: number, bucket: Bucket): Promise<WorkItem[]> {
  if (bucket === "running") {
    // ★1013 P8:首页「进行中」此前只认 xb_media_task —— 一键长图「读懂+排区块+派发」那约 1 分钟
    //   还没有任务行,首页看不到这单(/works 早已补上,见 pages/works/model/workLines.listRunning)。
    //   同一扇作业门补进来,口径与 /works 进行中一致(只收 QUEUED/RUNNING;近期失败卡留在 /works)。
    const [r, jobs] = await Promise.all([
      api.get<PageResult<RawPosterTask>>(`/poster/tasks?page=0&size=${size}&bucket=running`),
      listPosterOneClickInFlight(),
    ]);
    return [...(r.records ?? []).map(mapPoster), ...jobs];
  }
  const r = await api.get<PageResult<RawPosterTask>>(
    `/poster/tasks?page=0&size=${size}&bucket=${bucket}`
  );
  return (r.records ?? []).map(mapPoster);
}

interface RawOneClickJobRow {
  jobId: number;
  draftId?: number | null;
  status: string;
  createdAt?: string | null;
}

interface RawDraftRow {
  draftId: number;
  name?: string | null;
  formJson?: string | null;
}

/** 锚壳草稿 formJson 里的用途(与 features/poster canvasBridge.draftShellUseCase 同字段;非法值由 mapper 白名单挡)。 */
function shellUseCase(raw: string | null | undefined): string | null {
  try {
    const d = JSON.parse(raw ?? "") as { useCase?: unknown; canvasMeta?: { useCase?: unknown } };
    if (typeof d.useCase === "string" && d.useCase) return d.useCase;
    const u = d.canvasMeta?.useCase;
    return typeof u === "string" && u ? u : null;
  } catch {
    return null;
  }
}

/** 一键长图在途作业(还没有任务行的那段)。作业门/草稿门挂了 = 不补,不破整条「进行中」。 */
async function listPosterOneClickInFlight(): Promise<WorkItem[]> {
  const jobs = await api
    .get<RawOneClickJobRow[]>("/poster/longimage/oneclick/jobs")
    .catch((): RawOneClickJobRow[] => []);
  const live = (jobs ?? []).filter((j) => j.status === "QUEUED" || j.status === "RUNNING");
  if (live.length === 0) return [];
  const drafts = live.some((j) => j.draftId != null)
    ? await api.get<RawDraftRow[]>("/poster/drafts").catch((): RawDraftRow[] => [])
    : [];
  const byId = new Map((drafts ?? []).map((d) => [d.draftId, d]));
  return live.map((j) => {
    const d = j.draftId != null ? byId.get(j.draftId) : undefined;
    return mapPosterOneClickJob({
      jobId: j.jobId,
      draftId: j.draftId ?? null,
      status: j.status as "QUEUED" | "RUNNING",
      createdAt: j.createdAt ?? null,
      usage: shellUseCase(d?.formJson),
      draftName: d?.name ?? null,
    });
  });
}

async function listVideo(size: number, bucket: Bucket): Promise<WorkItem[]> {
  const r = await api.get<PageResult<RawVideoTask>>(
    `/video/tasks?page=0&size=${size}&bucket=${bucket}`
  );
  return (r.records ?? []).map(mapVideo);
}

async function listDh(size: number, bucket: Bucket): Promise<WorkItem[]> {
  const r = await api.get<PageResult<RawDhTask>>(
    `/digital-human/tasks?page=0&size=${size}&bucket=${bucket}`
  );
  return (r.records ?? []).map(mapDh);
}

/**
 * 官媒线(R2;codex P2:漏了这条 → hub 的「在途与最近」恒 null):
 * 后端只有轻列表(无 bucket),按草稿状态机客户端分桶 —— history=GENERATED/EDITED(mapReporter
 * 翻成 SUCCESS,恰好落 PRODUCT_BEARING),running=GENERATING。
 */
async function listReporter(size: number, bucket: Bucket): Promise<WorkItem[]> {
  const r = await api.get<RawReporterDraft[]>(`/tenant/reporter/drafts?limit=${size * 2}`);
  const wanted =
    bucket === "running" ? new Set(["GENERATING"]) : new Set(["GENERATED", "EDITED", "FAILED"]);
  return (r ?? [])
    .filter((d) => wanted.has(d.status))
    .map(mapReporter)
    .slice(0, size);
}

const FETCHERS: Partial<
  Record<WorkLineKey, (size: number, bucket: Bucket) => Promise<WorkItem[]>>
> = {
  poster: listPoster,
  video: listVideo,
  dh: listDh,
  jizhe: listReporter,
};

/** 该线是否已接线(记者线未接 → 调用方不渲染带)。 */
export function isLineWired(line: WorkLineKey): boolean {
  return !!FETCHERS[line];
}

/**
 * 「最近作品」只算**出得来产物**的终态(SUCCESS / PARTIAL)。
 *
 * ★这条带回答「我最近做出了什么」——失败 / 已取消 / 被拦截的任务不算「做出来」,
 * 混进来只会把真产物从 6 格里挤掉(codex review P2)。这是老 `RecentWorksStrip` 早有的
 * `DONE` 过滤,迁到 thin api 时被我漏掉了。
 * ★只在这条带过滤,不动 `/works` —— 那是检索视图,失败任务恰恰要看得见。
 */
const PRODUCT_BEARING = new Set(["SUCCESS", "PARTIAL"]);

/**
 * 线内子线过滤(分身舱一卡一页之后,三条线各自要一条「这条线的最近作品」)。
 * `key` 只用来进 queryKey —— 谓词是函数,同一条线的两种筛法必须缓存在两个键下。
 */
export interface RecentWorksFilter {
  key: string;
  predicate: (w: WorkItem) => boolean;
}

/** 带子线过滤时的取数倍率:三条子线最坏情况下一条只占 1/3,再留一档给失败任务。 */
const FILTERED_OVERFETCH = 6;

export async function listRecentWorks(
  line: WorkLineKey,
  size = RECENT_SIZE,
  filter?: RecentWorksFilter
): Promise<WorkItem[]> {
  const f = FETCHERS[line];
  if (!f) return [];
  // 多取一档再过滤:history 桶里混着失败任务,直接取 size 条过滤后可能不足 size。
  // 取 size×2 兜底,让常见的「近期有一两条失败」不至于把带压瘪。
  // ★带子线过滤时取 size×6:后端没有按 mode 筛的参数(只有 sceneKey,而 sceneKey 大半为 null),
  //   过滤只能在前端做 —— 取窄了会出现「这条线明明有作品,带却是空的」。
  //   仍然是**有界**的:再往前的作品去 `/works` 看,那才是检索视图。
  const raw = await f(size * (filter ? FILTERED_OVERFETCH : 2), "history");
  const done = raw.filter((w) => PRODUCT_BEARING.has(w.status));
  return (filter ? done.filter(filter.predicate) : done).slice(0, size);
}

/**
 * 在途任务(bucket=running)。工作台②「在途与最近」用。
 * ★与最近作品分两个请求而不是一个混着筛:后端 running / history 本来就是互斥两桶,
 * 混一起取再前端筛,会在「刚提交的任务」和「最近做完的」之间互相挤位置。
 */
export function listRunningWorks(line: WorkLineKey, size = 20): Promise<WorkItem[]> {
  const f = FETCHERS[line];
  if (!f) return Promise.resolve([]);
  return f(size, "running");
}
